import {HttpClient} from '@angular/common/http';
import {Injectable, TemplateRef} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {NavigationEnd, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {Observable, Subject} from 'rxjs';
import {take} from 'rxjs/operators';
import {
    MedicalCondition,
    MedicalConditionCodes
} from '../admin360/student-info/health-information/medical-condition/medical-condition.model';
import {AttendanceCode} from '../classroom360/attendance/models/attendance-code.model';
import {Category} from '../classroom360/gradebook/shared/models/category.model';
import {DynamicPagesForStudentsColumn} from '../classroom360/scheduled-export/dynamic-pages-for-students-column.model';
import {StudentNoteCategory} from '../classroom360/student-notes/student-note-category.model';
import {CurriculumMarkTypes} from '../student360/grades/grades.model';
import {Doctors, HealthNotes, Immunizations, Insurance} from '../student360/health/health.model';
import {MealService} from '../student360/meal-service/meal-service.model';
import {OnlinePaymentConfiguration} from '../student360/online-payment/models/online-payment-config.model';
import {StaffInfo} from '../student360/online-payment/models/staff-info.model';
import {Service} from '../student360/programs-and-services/service.model';
import {CourseTerm} from '../student360/student-schedule/course-term.model';
import {AboutTyler} from './controls/about-panel/about-tyler.model';
import {Attachment} from './models/attachment.model';
import {TylTranslation} from './models/tyl-translation.model';
import {UserDefinedField} from './models/user-defined-field.model';
import {Portal} from './portal.model';
import {Enrollment} from './student/enrollment.model';
import {System} from './System';
import {CSTField} from '../shared-modules/customization-framework/models/custom-data-field.model';
import { SystemLog } from './models/system-log.model';

class SnackbarData {
    label: string;
    buttonLabel: string;
    config: any;

    constructor(label: string, buttonLabel: string, config: any) {
        this.label = label;
        this.buttonLabel = buttonLabel;
        this.config = config;
    }
}

@Injectable()
export class SharedService {
    private _showAppHeader: boolean;
    public get showAppHeader(): boolean {
        if (this._showAppHeader == null) {
            return true;
        }
        return this._showAppHeader;
    }

    public set showAppHeader(value: boolean) {
        this._showAppHeader = value;
        this.observableShowAppHeader.next(value);
    }

    public observableShowAppHeader = new Subject<boolean>();
    observableShowAppHeaderStream$ = this.observableShowAppHeader.asObservable();

    private _pageToPdfTrigger: boolean;
    public get pageToPdfTrigger(): boolean {
        if (this._pageToPdfTrigger == null) {
            return true;
        }
        return this._pageToPdfTrigger;
    }

    public set pageToPdfTrigger(value: boolean) {
        this._pageToPdfTrigger = value;
        this.observablePageToPdfTrigger.next(value);
    }

    public observablePageToPdfTrigger = new Subject<boolean>();
    observablePageToPdfTrigger$ = this.observablePageToPdfTrigger.asObservable();

    private _progressBarPercent: number;
    public get progressBarPercent(): number {
        if (this._progressBarPercent == null) {
            return 0;
        }
        return this._progressBarPercent;
    }

    public set progressBarPercent(value: number) {
        this._progressBarPercent = value;
        this.observableProgressBarPercent.next(value);
    }

    public observableProgressBarPercent = new Subject<number>();
    observableProgressBarPercentStream$ = this.observableProgressBarPercent.asObservable();

    private _currentMedicalConditionChecked: boolean;
    public get currentMedicalConditionChecked(): boolean {
        return this._currentMedicalConditionChecked;
    }

    public set currentMedicalConditionChecked(value: boolean) {
        this._currentMedicalConditionChecked = value;
        this.observableCurrentMedicalConditionChecked.next(value);
    }

    public observableCurrentMedicalConditionChecked = new Subject<boolean>();
    observableobservableCurrentMedicalConditionCheckedStream$ = this.observableCurrentMedicalConditionChecked.asObservable();

    public observableClearStoredCustomFieldData = new Subject<boolean>();
    observableClearStoredCustomFieldDataStream$ = this.observableClearStoredCustomFieldData.asObservable();

    abortWasClicked = false;
    currentUrl: string;
    previousUrl: string;
    translations: TylTranslation[] = [];
    copyRecordData = new MedicalCondition();
    copyPhysicianRecord = new Doctors();
    copyInsuranceRecord = new Insurance();
    copyClicked = false;
    editRegistrationStudentMode: boolean;

    // These are properties only set by the menu - because that's the only way they can be set.
    // In other words, it tracks *menu selections* and only *menu selections*
    private _currentPortal: Portal;
    private observableCurrentPortal = new Subject<Portal>();
    observableCurrentPortalStream$ = this.observableCurrentPortal.asObservable();
    previousPortal: Portal;

    snackbarQueue: SnackbarData[] = [];
    snackbarShown = false;

    showFilter = false;
    filterTemplate: TemplateRef<any>;

    // Copy and Paste
    copiedData: string;
    copyIdentifier: string;

    constructor(
        private http: HttpClient,
        private router: Router,
        private snackbar: MatSnackBar,
        private translateService: TranslateService
    ) {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.previousUrl = this.currentUrl;
                this.currentUrl = event.url;
                this.closeFilter();
            }
        });
    }

    get currentPortal(): Portal {
        return this._currentPortal;
    }

    set currentPortal(value: Portal) {
        this.previousPortal = this._currentPortal;
        this._currentPortal = value;
        this.observableCurrentPortal.next(this._currentPortal);
    }

    public showSpinner = true;
    observableShowSpinner = new Subject<boolean>();
    observableShowSpinnerStream$ = this.observableShowSpinner.asObservable();

    public isPageLoaded = true;
    observableIsPageLoaded = new Subject<boolean>();
    observableIsPageLoadedStream$ = this.observableIsPageLoaded.asObservable();

    public currentComponent = null;

    toggleShowSpinner() {
        const newValue = !this.showSpinner;

        this.showSpinner = newValue;
        this.observableShowSpinner.next(newValue);
    }

    updateShowSpinner(newValue) {
        this.showSpinner = newValue;
        this.observableShowSpinner.next(newValue);
    }

    toggleIsPageLoaded() {
        const newValue = !this.isPageLoaded;

        this.isPageLoaded = newValue;
        this.observableIsPageLoaded.next(this.isPageLoaded);
    }

    updateIsPageLoaded(newValue) {
        this.isPageLoaded = newValue;
        this.observableIsPageLoaded.next(newValue);
    }

    getHTMLtoPDFBase64(html: HTMLElement): Observable<string> {
        return this.http.post<string>(System.apiURL.Student + 'GetHtmlToPDFBase64', [html.outerHTML]);
    }

    getAttachment(id: number, name: string): Observable<Attachment> {
        return this.http.get<Attachment>(System.apiURL.Student + `GetAttachment?attachmentId=${id}&screenName=${name}`);
    }

    showSnackbars() {
        if (!this.snackbarShown && this.snackbarQueue.length > 0) {
            this.snackbarShown = true;
            const snackbarData = this.snackbarQueue.pop();
            const openSnackbar = this.snackbar.open(
                snackbarData.label,
                snackbarData.buttonLabel,
                snackbarData.config
            );
            openSnackbar.afterDismissed().pipe(take(1)).subscribe(() => {
                this.snackbarShown = false;
                this.showSnackbars();
            });
        }
    }

    queueSnackbar(label: string, buttonLabel: string, config: any) {
        this.snackbarQueue.unshift(new SnackbarData(label, buttonLabel, config));
        this.showSnackbars();
    }

    showGoodSnackbar(label = 'Data successfully saved', buttonLabel = 'DISMISS'): void {
        label = this.translateService.instant(label);
        buttonLabel = this.translateService.instant(buttonLabel);
        this.queueSnackbar(label, buttonLabel, {
            horizontalPosition: 'right',
            panelClass: 'snackbarSuccess',
            duration: 5000
        });
    }

    showBadSnackbar(label = 'Data failed to save', buttonLabel = 'DISMISS'): void {
        label = this.translateService.instant(label);
        buttonLabel = this.translateService.instant(buttonLabel);
        this.queueSnackbar(label, buttonLabel, {
            horizontalPosition: 'right',
            panelClass: 'snackbarFailure',
            duration: 10000
        });
    }

    showCustomSnackbar(label: string, type: string, buttonLabel = 'DISMISS'): void {
        label = this.translateService.instant(label);
        buttonLabel = this.translateService.instant(buttonLabel);
        let style = 'snackbarSuccess';
        switch (type) {
            case 'success':
                style = 'snackbarSuccess';
                break;
            case 'failure':
                style = 'snackbarFailure';
                break;
            case 'notification':
                style = 'snackbarNotification';
                break;
        }
        this.queueSnackbar(label, buttonLabel, {
            horizontalPosition: 'right',
            panelClass: style,
            duration: 10000
        });
    }

    openFilter(filterTemplate: TemplateRef<any>) {
        this.filterTemplate = filterTemplate;
        this.showFilter = true;
    }

    closeFilter() {
        this.showFilter = false;
    }

    toggleFilter(filterTemplate: TemplateRef<any>) {
        if (this.showFilter) {
            this.closeFilter();
        } else {
            this.openFilter(filterTemplate);
        }
    }

    getErrorDetails(id: number, apiCall: string): Observable<string> {
        const queryString = '?userId=' + id;
        return this.http
            .post<string>(
                System.apiURL.Student + 'GetErrorDetails' + queryString,
                apiCall,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
    }

    generateError(): Observable<void> {
        return this.http.get<void>(System.apiURL.Shared + 'GenerateError');
    }

    saveWebPageAudit(studentId: number, schoolId: number, yearId: number, pageName: string) {
        const queryString = '?studentId=' + studentId +
            '&schoolId=' + schoolId +
            '&yearId=' + yearId +
            '&pageName=' + pageName;
        return this.http.get(System.apiURL.Student + 'SaveWebPageAudit' + queryString);
    }

    getAboutTylerSIS() {
        return this.http.get<AboutTyler>(System.apiURL.Student + 'GetAboutTylerSIS');
    }

    createPulseKey(page: string, filter: string, embed: boolean) {
        return this.http.get(System.apiURL.Student + 'CreatePulseKey?page=' + page + '&filter=' + filter + '&embed=' + embed);
    }

    getReEnrollStudentsList(yearId: number, familyId: number, studentId: number): Observable<Enrollment[]> {
        return this.http.get<Enrollment[]>(System.apiURL.Student + 'GetReEnrollStudentsList?yearId=' + yearId + '&familyId=' + familyId + '&studentId=' + studentId);
    }

    markStudentsAsNew(yearId: number, familyId: number, studentIds: string, gradeLevelIds: string) {
        const queryString = '?yearId=' + yearId +
            '&familyId=' + familyId +
            '&studentIds=' + studentIds +
            '&gradeLevelIds=' + gradeLevelIds;
        return this.http.get(System.apiURL.Student + 'MarkReEnrolStudentsAsNew' + queryString);
    }

    getUserDefinedField(fieldName: string, isSysGen: boolean) {
        const queryString = '?fieldName=' + fieldName +
            '&isSysGen=' + isSysGen;
        return this.http.get<UserDefinedField>(System.apiURL.Student + 'GetUserDefinedField' + queryString);
    }

    getCurriculumMarkTypesReadForSubject(siteSectionId: number): Observable<CurriculumMarkTypes[]> {
        return this.http.get<CurriculumMarkTypes[]>(System.apiURL.Classroom + `GetCurriculumMarkTypesReadForSubject?courseId=${siteSectionId}`);
    }

    getCategories(isTeacherUser: boolean, courseId: number, courseIds: string): Observable<any> {
        return this.http.get<Category[]>(System.apiURL.Classroom + `GetGradebookAssignmentType?isTeacherUser=${isTeacherUser}&courseId=${courseId}&courseList=${courseIds}`);
    }

    getMealService(studentId: number, yearId: number, siteYearId: number) {
        return this.http.get<MealService[]>(System.apiURL.Classroom + `GetMealServices?studentId=${studentId}&yearId=${yearId}&siteYearId=${siteYearId}`);
    }

    getStaffInfoByUserId(): Observable<any> {
        return this.http.get<StaffInfo[]>(System.apiURL.Classroom + 'GetStaffInfoByUserId');
    }

    getStudentNoteCategories(schoolId: number, yearId: number): Observable<StudentNoteCategory[]> {
        return this.http.get<StudentNoteCategory[]>(System.apiURL.Classroom + `GetStudentNoteCategories?schoolId=${schoolId}&yearId=${yearId}`);
    }

    getOnlinePaymentConfiguration(): Observable<OnlinePaymentConfiguration> {
        return this.http.get<OnlinePaymentConfiguration>(System.apiURL.Classroom + 'GetOnlinePaymentConfiguration');
    }

    getAttendanceCodeLookup(isTeacher: boolean, schoolId: number, yearId: number): Observable<AttendanceCode[]> {
        return this.http.get<AttendanceCode[]>(System.apiURL.Counselor + `GetAttendanceCodeLookup?isTeacher=${isTeacher}&schoolId=${schoolId}&yearId=${yearId}`);
    }

    getDynamicPagesReadForStudentLookup(userIsTeacher: boolean): Observable<any> {
        return this.http.get<DynamicPagesForStudentsColumn[]>(System.apiURL.Classroom + `GetDynamicPagesReadForStudentLookup?userIsTeacher=${userIsTeacher}`);
    }

    getGradebookTermsBySiteSectionId(siteSectionId: number, tgsId: number, showTermOnly: boolean, yearId: number, schoolId: number, userId: number): Observable<CourseTerm[]> {
        const apiCallString = System.apiURL.Classroom + 'GetGradebookTermsBySiteSectionId?siteSectionId=' + siteSectionId
            + '&tgsId=' + tgsId
            + '&showTermOnly=' + showTermOnly
            + '&yearId=' + yearId
            + '&schoolId=' + schoolId
            + '&userId=' + userId;
        return this.http.get<CourseTerm[]>(apiCallString);
    }

    public getAllServices(): Observable<Service[]> {
        return this.http.get<Service[]>(System.apiURL.Student + `GetAllServices`);
    }

    getMedicalConditionByStudentId(studentId: number, siteId: number, yearId: number): Observable<MedicalCondition[]> {
        return this.http.get<MedicalCondition[]>(System.apiURL.Shared + `GetMedicalConditionReadByStudentId?studentId=${studentId}&siteId=${siteId}&yearId=${yearId}`);
    }

    deleteMedicalConditionById(medicalConditionId: number): Observable<boolean> {
        return this.http.get<boolean>(System.apiURL.Shared + `DeleteMedicalConditionById?medicalConditionId=${medicalConditionId}`);
    }

    public saveMedicalCondition(studentId: number, siterYearId: number, data: MedicalCondition): Observable<void> {
        return this.http.post<void>(System.apiURL.Shared + `SaveMedicalCondition?studentId=${studentId}&siterYearId=${siterYearId}`, data);
    }

    getMedicalConditionCodes(): Observable<MedicalConditionCodes[]> {
        return this.http.get<MedicalConditionCodes[]>(System.apiURL.Shared + `GetMedicalConditionCodes`);
    }

    // Delete Immunizations
    deleteImmunizationById(immunizationId: number, yearId: number): Observable<boolean> {
        let queryString = '';
        queryString += '?immunizationId=' + immunizationId +
            '&yearId=' + yearId;
        return this.http.get<boolean>(System.apiURL.Shared + 'DeleteImmunizationById' + queryString);
    }

    deleteDoctorById(physiciansId: number): Observable<boolean> {
        return this.http.get<boolean>(System.apiURL.Shared + `DeleteDoctorById?physiciansId=${physiciansId}`);
    }

    saveMedicalPhysicianInformation(studentId: number, data: Doctors): Observable<void> {
        return this.http.post<void>(System.apiURL.Shared + `SaveMedicalPhysicianInformation?studentId=${studentId}`, data);
    }

    // Save Insurance
    saveInsurance(studentId: number, insuranceData: Insurance): Observable<boolean> {
        const queryString = '?studentId=' + studentId;
        return this.http.post<boolean>(System.apiURL.Student + 'SaveInsurance' + queryString, insuranceData);
    }

    // Delete Insurance
    deleteInsurance(physiciansId: number, insuranceId: number): Observable<boolean> {
        const queryString = '?physiciansId=' + physiciansId + '&insuranceId=' + insuranceId;
        return this.http.get<boolean>(System.apiURL.Student + 'DeleteInsurance' + queryString);
    }

    // Get Health Notes
    getHealthNotes(studentId: number): Observable<HealthNotes> {
        const queryString = '?studentId=' + studentId;
        return this.http.get<HealthNotes>(System.apiURL.Student + 'GetHealthNotes' + queryString);
    }

    // Save Health Notes
    saveHealthNotes(noteType: string, healthNotes: HealthNotes): Observable<boolean> {
        const queryString = '?noteType=' + noteType;
        return this.http.post<boolean>(System.apiURL.Student + 'SaveHealthNotes' + queryString, healthNotes);
    }

    // Save Immunization
    saveImmunization(studentId: number, yearId: number, schoolId: number, immunizationData: Immunizations): Observable<void> {
        const queryString = '?studentId=' + studentId
            + '&yearId=' + yearId
            + '&schoolId=' + schoolId;
        return this.http.post<void>(System.apiURL.Student + 'SaveImmunization' + queryString, immunizationData);
    }

    getCalendarUseAttendanceTypes(schoolId: number, yearId: number, studentId: number): Observable<boolean> {
        const queryString = '?schoolId=' + schoolId
            + '&yearId=' + yearId
            + '&studentId=' + studentId;
        return this.http.get<boolean>(System.apiURL.Shared + 'GetCalendarUseAttendanceTypes' + queryString);
    }

    getCustomDataFieldsByObjectID(objectID: number): Observable<CSTField[]> {
        const queryString = '?objectID=' + objectID;
        return this.http.get<CSTField[]>(System.apiURL.Admin + 'GetCustomDataFieldsByObjectID' + queryString);
    }

    createImpersonationLog(impersonateUserId: number, userType = 2) {
        return this.http.get<number>(System.apiURL.Admin + 'CreateImpersonationLog?impersonateUserId=' + impersonateUserId + '&userTypeId=' + userType);
    }

    createSystemLog(log: SystemLog): Observable<void> {
        return this.http.post<void>(System.apiURL.Shared + 'CreateSystemLog', log);
    }

    // Copying and Pasting Fields
    simpleCopy(fields: string[], object: any, identifier: string) {
        // PRE: a set of fields for the objects that will be made into JSON. The identifier determines what the name of the object is.
        // POST: creates a JSON string for the set of objects with the specified fields
        // NOTE: will not copy objects - you have to use the complex copy for that
        const jsonItem: {[key: string]: any} = {};
        fields.forEach(field => {
            if (typeof object[field] != 'object' || object[field] instanceof Date) {
                jsonItem[field] = object[field];
            }
        });
        this.copiedData = JSON.stringify(jsonItem);
        this.copyIdentifier = identifier;

        // Sending snackbar
        this.showGoodSnackbar('Fields copied!');
    }

    complexCopy(fields: any, object: any, identifier: string) {
        // PRE: a set of objects to make JSON out of. The fields are specified as key-value pairs of booleans or key-value pairs.
        //      for example: fields = {"A": true, "B": {"SubA": true, "SubB": false"}} and objects = [{"A": 123, "B": {"SubA": 456, "SubB": 789}}]
        //      In this case, "B" is the name of a child object with fields "SubA" and "SubB"
        //      Any field that is set to "true" will be put in the JSON
        //      However, a child *object* must have the fields for that key also be an *object*
        //      In the previous example, "B" will *not* print if the object = {"A": 123, "B": 456}
        //      The identifier determines what the name of the object is.
        // POST: makes the JSON out of the specified fields and objects

        // Helper Function
        const createJSONObject = (createFields: any, createObject: any) => {
            const jsonItem: {[key: string]: any} = {};
            const fieldNames = Object.keys(createFields);
            fieldNames.forEach(fieldName => {
                if (createFields[fieldName]) {

                    // If it is an object, create a JSON object for it and add it to the item
                    if (
                        typeof createFields[fieldName] == 'object'
                        && typeof createObject[fieldName] == 'object'
                    ) {
                        jsonItem[fieldName] = createJSONObject(createFields[fieldName], createObject[fieldName]);
                    }

                    // If it is a boolean, add it's raw value
                    else if (
                        typeof createFields[fieldName] == 'boolean'
                        && (
                            typeof createObject[fieldName] != 'object'
                            || createObject[fieldName] instanceof Date
                        )
                    ) {
                        jsonItem[fieldName] = createObject[fieldName];
                    }
                }
            });
            return jsonItem;
        };

        // Constructing the JSON
        this.copiedData = JSON.stringify(createJSONObject(fields, object));
        this.copyIdentifier = identifier;

        // Sending snackbar
        this.showGoodSnackbar('Fields copied!');
    }

    paste(objectToCopyTo?: any) {
        const parsedObject = JSON.parse(this.copiedData);
        if (objectToCopyTo) {
            Object.assign(objectToCopyTo, parsedObject);
        }
        return parsedObject;
    }
}
